import chunk from "lodash/chunk";

export interface ChunkedQueueOptions<T> {
  chunkSize: number;
  interval: number;
  presist?: { get(): Promise<T[]>; set(queue: T[]): Promise<void> };
}

export class ChunkedQueue<T> {
  private queue: T[] = [];
  private timer: NodeJS.Timeout | null = null;
  private initializationPromise: Promise<void>;

  constructor(
    private readonly onChunk: (chunk: T[]) => Promise<void>,
    private readonly options: ChunkedQueueOptions<T>
  ) {
    this.initializationPromise = this.init();
  }

  public async init(): Promise<void> {
    try {
      if (this.options.presist) {
        this.queue = await this.options.presist.get();
      }
    } catch (e) {
      this.queue = [];
    }
  }

  private async savePersistent() {
    if (this.options.presist) {
      await this.options.presist.set(this.queue);
    }
  }

  public async enqueue(item: T): Promise<void> {
    await this.initializationPromise;
    this.queue.push(item);
    await this.savePersistent();

    if (this.queue.length >= this.options.chunkSize) {
      await this.flush();
    } else if (this.timer === null) {
      this.timer = setTimeout(() => this.flush(), this.options.interval);
    }
  }

  get size(): number {
    return this.queue.length;
  }

  public async clear() {
    await this.initializationPromise;
    const chunk = this.queue;
    this.queue = [];
    await this.savePersistent();
    return chunk;
  }

  public async flush(): Promise<void> {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (this.queue.length === 0) {
      return;
    }

    await this.initializationPromise;
    const chunks = chunk(this.queue, this.options.chunkSize);
    this.queue = [];
    await this.savePersistent();

    for (const chunk of chunks) {
      await this.onChunk(chunk);
    }
  }
}
